import { CloseOutlined, DownCircleOutlined } from '@ant-design/icons';
import {
  BarometerInHg,
  CloudCoverPercent,
  FishingReport,
  MapSourceTile,
  OmniaPagination,
  Waterbody,
  WaterbodyDetail,
  WaterbodyPin,
  Waypoint,
  WindDirection,
  WindMph,
} from '@omniafishing/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Modal } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MapRef, MarkerDragEvent, ViewState } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useExperiment } from 'statsig-react';
import { LoadableMap } from '../../components/map/loadable_omnia_map';
import { BaseModal } from '../../components/map/map_modals/base_modal/base_modal';
import { BoatLandingModal } from '../../components/map/map_modals/boat_landing_modal/boat_landing_modal';
import { FeaturedHotbaitsModal } from '../../components/map/map_modals/featured_hotbaits_modal/featured_hotbaits_modal';
import { FeaturedReportsModal } from '../../components/map/map_modals/featured_reports_modal/featured_reports_modal';
import { ForecastModal } from '../../components/map/map_modals/forecast_modal/forecast_modal';
import { LakesModal } from '../../components/map/map_modals/lakes_modal/lakes_modal';
import { LayersModal } from '../../components/map/map_modals/layers_modal/layers_modal';
import { LayersQuickToggleMenu } from '../../components/map/map_modals/layers_quick_toggle_modal/layers_quick_toggle_menu';
import { LegendModal } from '../../components/map/map_modals/legend_modal/legend_modal';
import { MobileContentToggleMenu } from '../../components/map/map_modals/mobile_content_toggle_modal/mobile_content_toggle_menu';
import { MyLakesModal } from '../../components/map/map_modals/my_lakes_modal/my_lakes_modal';
import { PinFiltersModal } from '../../components/map/map_modals/pin_filters_modal/pin_filters_modal';
import { PointsOfInterestModal } from '../../components/map/map_modals/points_of_interest_modal/points_of_interest_modal';
import { WaterClaritySliderModal } from '../../components/map/map_modals/water_clarity_slider_modal/water_clarity_slider_modal';
import { WaterLayersModal } from '../../components/map/map_modals/water_layers_modal/water_layers_modal';
import { WaterTempSliderModal } from '../../components/map/map_modals/water_temp_slider_modal/water_temp_slider_modal';
import {
  FiltersScreenProps,
  WaypointType,
} from '../../components/map/map_modals/waypoints_modal/filters_screen';
import { WaypointsModal } from '../../components/map/map_modals/waypoints_modal/waypoints_modal';
import { truncateTo6Decimal } from '../../components/map/map_modals/waypoints_modal/waypoint_form_helper';
import { WeatherLayerModal } from '../../components/map/map_modals/weather_layers_modal/weather_layers_modal';
import {
  AerisController,
  CORE_LAYER,
  isInteractiveWaterbodyFeature,
  isWaterClarityFeature,
  isWaterTempFeature,
  LAYER_TYPES,
  MapboxBoatLanding,
  MapLayerState,
  POINTS_OF_INTEREST_LAYER,
  WEATHER_LAYER,
} from '../../components/map/map_types';
import { debouncedMapEventTracking } from '../../components/map/map_utils';
import { MapBounds } from '../../components/map/omnia_map';
import { InteractiveLayerIds } from '../../components/map/omnia_map_sources';
import { DESKTOP_PINS_LIMIT, MOBILE_PINS_LIMIT } from '../../components/map_default/map_default';
import { useMapLayerState } from '../../components/map_default/use_map_layer_state';
import { WaterClarityPopup } from '../../components/map_default/water_clarity_popup';
import { WaterTempPopup } from '../../components/map_default/water_temp_popup';
import { OmniaButton } from '../../components/omnia_button/omnia_button';
import { MobileOnly } from '../../components/responsive_elements/responsive_elements';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { LoadingState } from '../../constants/loading_state';
import { QueryKeys } from '../../constants/query_keys';
import { useAfterMountedEffect } from '../../hooks/use_after_mounted_effect';
import { useDimensions } from '../../hooks/use_dimensions';
import { useFishingReportListViewed } from '../../hooks/use_fishing_report_list_viewed';
import { usePrevious } from '../../hooks/use_previous';
import { useProUpsellModal } from '../../hooks/use_pro_upsell_modal';
import { omniaStringify, useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { useUser } from '../../hooks/use_user';
import { useUserWaterbodies } from '../../hooks/use_user_waterbodies';
import { useWaypoints } from '../../hooks/use_waypoints';
import { apiV1 } from '../../lib/api';
import { fitBounds } from '../../lib/fit_bounds';
import { GlobalQueryParams, OmniaQueryParams } from '../../lib/query_string';
import { WaypointFilterType, WebAnalytics } from '../../lib/web_analytics';
import {
  getLatitude,
  getLongitude,
  getZoom,
  LocationActions,
} from '../../redux/geographic_location';
import { getSeasonGroups, getSpecies } from '../../redux/reference_data';
import { RoutePaths } from '../../routes';
import { SvgNavFishingReports } from './assets/svg_nav_fishing_reports';
import { SvgNavHotBaits } from './assets/svg_nav_hotbaits';
import { SvgNavMapPins } from './assets/svg_nav_map_pins';
import { SvgNavMyLakes } from './assets/svg_nav_my_lakes';
import { SvgNavMyWaypoints } from './assets/svg_nav_my_waypoints';
import { SvgNavTerrainView } from './assets/svg_nav_terrain_view';
import { SvgNavWaterLayers } from './assets/svg_nav_water_layers';
import { SvgNavWeatherLayers } from './assets/svg_nav_weather_layers';
import {
  adjustLatLng,
  areBoundsZero,
  getClosedModalBounds,
  getOpenModalBounds,
  getPinLimit,
  getUpdatedModalType,
  MINIMUM_ZOOM,
  TEMP_TILE_SELECTED_COVERAGE_PERCENT,
  tileMeetsMinimumCoverage,
} from './lib/map_page_helpers';
import { ActiveMapPageModalType, ExtendedMapQueryParams } from './lib/map_page_types';
import { useSpeciesSeasonGroupFilters } from './lib/use_species_season_group_filters';
import styles from './map_page.less';
import { MapPageSEO } from './map_page_seo';
import { SearchAndFilters } from './search_and_filters';

interface MapPageProps {
  headerHeight: number;
  modalContent?: React.ReactNode;
  modalWaterbody?: WaterbodyDetail;
  seo?: React.ReactNode;
  sharedWaypoint: Waypoint;
  waterbody: WaterbodyDetail;
}

const MapPage = (props: MapPageProps) => {
  const { headerHeight, modalContent, modalWaterbody, seo } = props;
  const { getCurrentQuery, replaceQueryString, pushQueryString, paramsString } = useQueryString();
  const currentQuery = getCurrentQuery<ExtendedMapQueryParams>();
  const speciesQuery = currentQuery[OmniaQueryParams.species];
  const seasonGroupQuery = currentQuery[OmniaQueryParams.season_group];
  const waterbodyQuery = currentQuery.waterbody_slug;
  const sharedWaypointQuery = currentQuery[GlobalQueryParams.waypoint_hash];
  const initialModalQueryParam = currentQuery.initial_modal;
  const lunkerQuery = !!currentQuery.lunker;
  const highAbundanceQuery = !!currentQuery.high_abundance;
  const lngQuery = currentQuery.lng;
  const latQuery = currentQuery.lat;
  const zoomQuery = currentQuery.zoom;
  const externalSearchQuery = currentQuery.external_search;
  const pinFilterCount = [lunkerQuery, speciesQuery, seasonGroupQuery].filter(Boolean).length;
  const location = useLocation<{
    mapLayerState: MapLayerState;
    selectedBoatLanding: MapboxBoatLanding;
  }>();
  const initialMapLayerState = location.state?.mapLayerState;
  const initialSelectedBoatLanding = location.state?.selectedBoatLanding;

  const { isMobile, isDesktop } = useResponsive();

  const dispatch = useDispatch();
  const reduxLat = useSelector(getLatitude);
  const reduxLng = useSelector(getLongitude);
  const reduxZoom = useSelector(getZoom);
  const { isPro } = useUser();
  const { openProUpsellModal } = useProUpsellModal();

  const [mapRef, setMapRef] = useState<MapRef>();
  const [aerisController, setAerisController] = useState<AerisController>(null);

  const [queryableBounds, setQueryableBounds] = useState<MapBounds>({
    e: 0,
    w: 0,
    n: 0,
    s: 0,
  });

  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });
  const [measureRef, measureBounds] = useDimensions({
    onResize: true,
    onElementResize: true,
  });

  useLayoutEffect(() => {
    handleDimensionsChange(measureBounds.width, measureBounds.height);
  }, [measureBounds.width, measureBounds.height, mapRef]);

  const [modalContentOpen, setModalContentOpen] = useState<boolean>(modalContent != null);
  const history = useHistory();

  const longitude = Number(lngQuery) || reduxLng || -93.5519097201729;
  const latitude = Number(latQuery) || reduxLat || 44.9290887635944;
  const zoom = Number(zoomQuery) || reduxZoom;
  const currentViewport = useMemo(() => {
    return {
      longitude,
      latitude,
      zoom,
    };
  }, [longitude, latitude, zoom]);

  useEffect(() => {
    if (lngQuery || latQuery || zoomQuery || sharedWaypointQuery) {
      return;
    }
    replaceQueryString<ExtendedMapQueryParams>({
      lng: truncateTo6Decimal(reduxLng),
      lat: truncateTo6Decimal(reduxLat),
      zoom: reduxZoom,
    });
  }, []);

  const allSpecies = useSelector(getSpecies);
  const allSeasonGroups = useSelector(getSeasonGroups);
  const selectedSpecies = useMemo(
    () => allSpecies.find((s) => s.name === speciesQuery),
    [speciesQuery, allSpecies]
  );
  const selectedSeasonGroup = useMemo(
    () => allSeasonGroups.find((s) => s.name === seasonGroupQuery),
    [seasonGroupQuery, allSeasonGroups]
  );
  const [searchIsOpen, setSearchIsOpen] = useState(false);

  const [selectedBoatLanding, setSelectedBoatLanding] = useState<MapboxBoatLanding>(
    initialSelectedBoatLanding
  );
  const [selectedFishingReport, setSelectedFishingReport] = useState<FishingReport | null>(null);
  const [selectedWaterbody, setSelectedWaterbody] = useState<WaterbodyDetail>(props.waterbody);
  const [highlightedWaterbody, setHighlightedWaterbody] = useState<Waterbody>(null);
  const [waterbodyLoadingState, setWaterbodyLoadingState] = useState<LoadingState>(
    LoadingState.NOT_STARTED
  );
  const [selectedWaypoint, setSelectedWaypoint] = useState<Waypoint | null>(null);
  const [sharedWaypoint, setSharedWaypoint] = useState<Waypoint | null>(props.sharedWaypoint);
  const [showWaypointDetailsBackButton, setShowWaypointDetailsBackButton] =
    useState<boolean>(false);

  const [newWaypoint, setNewWaypoint] =
    useState<Pick<Waypoint, 'lat' | 'lng' | 'waypoint_type' | 'name'>>(null);
  const [editableWaypoint, setEditableWaypoint] = useState<Waypoint>(null);

  const [waterTempTiles, setWaterTempTiles] = useState<MapSourceTile[]>([]);
  const [waterTempTilesLoadingState, setWaterTempTilesLoadingState] = useState<LoadingState>(
    LoadingState.NOT_STARTED
  );
  const [waterTempSliderIdx, setWaterTempSliderIdx] = useState<number>(0);
  const [waterTempShowComposite, setWaterTempShowComposite] = useState<boolean>(true);
  const waterTempSliderTile = waterTempTiles[waterTempSliderIdx];
  const waterTempDisplayedDate =
    waterTempSliderTile != null ? dayjs(waterTempSliderTile.date) : null;
  const [waterTempModalOpen, setWaterTempModalOpen] = useState(false);

  const [waterClarityTiles, setWaterClarityTiles] = useState<MapSourceTile[]>([]);
  const [waterClarityTilesLoadingState, setWaterClarityTilesLoadingState] = useState<LoadingState>(
    LoadingState.NOT_STARTED
  );
  const [waterClaritySliderIdx, setWaterClaritySliderIdx] = useState<number>(0);
  const [waterClarityShowComposite, setWaterClarityShowComposite] = useState<boolean>(true);
  const waterClaritySliderTile = waterClarityTiles[waterClaritySliderIdx];
  const waterClarityDisplayedDate =
    waterClaritySliderTile != null ? dayjs(waterClaritySliderTile.date) : null;
  const [waterClarityModalOpen, setWaterClarityModalOpen] = useState(false);

  const [interactiveWaterbodyId, setInteractiveWaterbodyId] = useState<string | null>(null);
  const [interactiveWaterbody, setInteractiveWaterbody] = useState<Waterbody | null>();
  const prevInteractiveWaterbodyIdRef = useRef<string | null>(null);

  const {
    setBaseLayer,
    onSelectCoreLayer,
    onSelectOverlayLayer,
    onSelectWeatherLayer,
    onSelectPointsOfInterestLayer,
    mapLayerState,
  } = useMapLayerState(initialMapLayerState);

  const omniaWaterTempLayerActive =
    mapLayerState?.[LAYER_TYPES.CORE] === CORE_LAYER.OMNIA_WATER_TEMP;
  const omniaWaterClarityLayerActive = mapLayerState?.[LAYER_TYPES.CORE] === CORE_LAYER.CLARITY;
  const hasForecastableLayer =
    _.intersection(
      [WEATHER_LAYER.AIR_TEMP, WEATHER_LAYER.WIND, WEATHER_LAYER.RADAR],
      mapLayerState?.[LAYER_TYPES.WEATHER]
    ).length > 0;

  const defaultPanel = useExperiment('map_default_panel').config.get<
    'featuredReports' | 'hotbaits'
  >('default_panel', 'featuredReports');

  const initialModal = sharedWaypoint
    ? 'waypoints'
    : isMobile
    ? null
    : initialModalQueryParam || defaultPanel;

  const [activeModal, setActiveModal] = useState<ActiveMapPageModalType>(initialModal);
  const prevActiveModal = usePrevious(activeModal);
  const isReportsModalOpen = activeModal === 'featuredReports';
  const isLayersModalOpen = activeModal === 'layers';
  const isLegendModalOpen = activeModal === 'legend';
  const isLakesModalOpen = activeModal === 'lakes';
  const isWaypointModalOpen = activeModal === 'waypoints';
  const isBaseModalOpen = activeModal === 'base';
  const isWeatherModalOpen = activeModal === 'weather';
  const isPointsOfInterestModalOpen = activeModal === 'points_of_interest';
  const isCoreModalOpen = activeModal === 'core';
  const isMyLakesModalOpen = activeModal === 'my_lakes';
  const isHotBaitsModalOpen = activeModal === 'hotbaits';

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const [selectedWaterbodyIds, setSelectedWaterbodyIds] = useState<string[]>([]);
  const [selectedWaypointTypes, setSelectedWaypointTypes] = useState<WaypointType[]>([]);
  const [selectedBarometricPressures, setSelectedBarometricPressures] = useState<BarometerInHg[]>(
    []
  );
  const [selectedBarometricPressureTrends, setSelectedBarometricPressureTrends] = useState<
    Waypoint['barometer_trend'][]
  >([]);
  const [selectedCloudCovers, setSelectedCloudCovers] = useState<CloudCoverPercent[]>([]);
  const [selectedWindDirections, setSelectedWindDirections] = useState<WindDirection[]>([]);
  const [selectedWindSpeeds, setSelectedWindSpeeds] = useState<WindMph[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<string | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);

  const { waypointsFiltered, waypointsFilteredLoading, waypointsAll, waypointsAllLoading } =
    useWaypoints({
      waypointsFilteredFetchParams: {
        barometer_in_hgs: selectedBarometricPressures,
        barometer_trends: selectedBarometricPressureTrends,
        cloud_cover_percents: selectedCloudCovers,
        end_date: selectedEndDate,
        start_date: selectedStartDate,
        waterbodies: selectedWaterbodyIds,
        waypoint_types: selectedWaypointTypes,
        wind_directions: selectedWindDirections,
        wind_mphs: selectedWindSpeeds,
      },
    });

  useEffect(
    function waypointFiltersTrack() {
      const trackFilterChange = () => {
        const filterValues: WaypointFilterType[] = [];
        if (selectedWaypointTypes.length > 0) {
          filterValues.push('waypoint_type');
        }
        if (selectedWaterbodyIds.length > 0) {
          filterValues.push('waterbody');
        }
        if (selectedStartDate && selectedEndDate) {
          filterValues.push('date_created');
        }
        if (selectedWindDirections.length > 0) {
          filterValues.push('wind_direction');
        }
        if (selectedWindSpeeds.length > 0) {
          filterValues.push('wind_speed');
        }
        if (selectedCloudCovers.length > 0) {
          filterValues.push('cloud_cover');
        }
        if (selectedBarometricPressures.length > 0) {
          filterValues.push('barometric_pressure');
        }
        if (selectedBarometricPressureTrends.length > 0) {
          filterValues.push('barometric_pressure_trend');
        }

        WebAnalytics.waypointFiltered({ filter_values: filterValues });
      };

      if (
        selectedWaypointTypes.length ||
        selectedWaterbodyIds.length ||
        (selectedStartDate && selectedEndDate)
      ) {
        trackFilterChange();
      }
    },
    [
      selectedWaypointTypes,
      selectedWaterbodyIds,
      selectedStartDate,
      selectedEndDate,
      selectedWindDirections,
      selectedWindSpeeds,
      selectedCloudCovers,
      selectedBarometricPressures,
      selectedBarometricPressureTrends,
    ]
  );

  const [markers, setMarkers] = useState<WaterbodyPin[]>([]);
  const [waterbodiesLoadingState, setWaterbodiesLoadingState] = useState<LoadingState>(
    LoadingState.NOT_STARTED
  );

  const fetchPinsDisabled = useRef(false);

  const fetchLakes = (bounds: MapBounds, zoomParam: number) => {
    if (fetchPinsDisabled.current || bounds == null) {
      return;
    }

    if (bounds.e === 0 && bounds.n === 0 && bounds.s === 0 && bounds.w === 0) {
      return;
    }

    const desktopPinsToFetch = getPinLimit(Number(zoomParam));
    const fetchParams: Parameters<typeof apiV1.mapPinsFetch>[0] = {
      ...bounds,
      limit: isMobile ? MOBILE_PINS_LIMIT : desktopPinsToFetch,
      species: selectedSpecies?.name,
      season_group: selectedSeasonGroup?.name,
      high_abundance: highAbundanceQuery,
      lunker: lunkerQuery,
    };

    setWaterbodiesLoadingState(LoadingState.PENDING);
    apiV1
      .mapPinsFetch(fetchParams)
      .then((response) => {
        setMarkers(response.data.data);
        setWaterbodiesLoadingState(LoadingState.DONE);
      })
      .catch((error) => {
        setWaterbodiesLoadingState(LoadingState.ERROR);
        console.log(`ERROR: fetchPins`, error);
      });
  };

  const fetchLakesDebounced = useCallback(
    _.debounce((bounds: MapBounds, zoomParam: number) => fetchLakes(bounds, zoomParam), 300),
    [
      selectedSpecies?.name,
      selectedSeasonGroup?.name,
      isMobile,
      fetchPinsDisabled.current,
      mapRef,
      highAbundanceQuery,
      lunkerQuery,
    ]
  );

  const [mapBounds, setMapBounds] = useState<MapBounds>({
    n: 0,
    e: 0,
    s: 0,
    w: 0,
  });

  useEffect(() => {
    fetchLakesDebounced(mapBounds, zoom);
  }, [
    mapBounds.e,
    mapBounds.n,
    mapBounds.s,
    mapBounds.w,
    selectedSpecies?.name,
    selectedSeasonGroup?.name,
    zoom,
    highAbundanceQuery,
    lunkerQuery,
  ]);

  const { filterSeasonGroup, filterSpecies } = useSpeciesSeasonGroupFilters();

  const mapFishingReportsQueryParams: Parameters<typeof apiV1.mapFishingReportsFetch>[0] = {
    ...queryableBounds,
    species: filterSpecies?.name,
    season_group: filterSeasonGroup?.name,
    per_page: 7,
    waterbodies_limit: isMobile
      ? MOBILE_PINS_LIMIT
      : zoom != null
      ? getPinLimit(Number(zoom))
      : DESKTOP_PINS_LIMIT,
  };
  const mapFishingReportsQuery = useInfiniteQuery<{
    fishingReports: FishingReport[];
    metadata: OmniaPagination & {
      count_total: number;
    };
  }>({
    queryKey: QueryKeys.MAP.FISHING_REPORTS(mapFishingReportsQueryParams),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const { metadata } = lastPage;
      const hasMorePages = metadata.count_total > pages.length * metadata.per_page;
      return hasMorePages ? lastPage.metadata.page + 1 : undefined;
    },
    queryFn: async (params) => {
      const { pageParam } = params;
      const res = await apiV1.mapFishingReportsFetch({
        ...mapFishingReportsQueryParams,
        page: pageParam as number,
      });
      return {
        fishingReports: res.data.data,
        metadata: res.data.metadata,
      };
    },
    staleTime: 0,
    enabled: !areBoundsZero(queryableBounds),
  });

  const fishingReports =
    mapFishingReportsQuery.data?.pages.flatMap((page) => page.fishingReports) || [];
  const fishingReportsLoading = mapFishingReportsQuery.isLoading;
  const fihsingReportsLoadingComplete = mapFishingReportsQuery.isSuccess;
  const fishingReportsCountTotal = mapFishingReportsQuery.data?.pages[0]?.metadata.count_total;

  const observer = useRef<IntersectionObserver>();
  const loadMoreElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (mapFishingReportsQuery.isLoading) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0]?.isIntersecting &&
          mapFishingReportsQuery.hasNextPage &&
          !mapFishingReportsQuery.isFetching
        ) {
          mapFishingReportsQuery.fetchNextPage();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [
      mapFishingReportsQuery.fetchNextPage,
      mapFishingReportsQuery.hasNextPage,
      mapFishingReportsQuery.isFetching,
      mapFishingReportsQuery.isLoading,
    ]
  );

  useFishingReportListViewed(fishingReports, 'embedded', 0, fishingReports.length);

  useEffect(() => {
    if (mapRef && mapBounds.e && mapBounds.n && mapBounds.s && mapBounds.w) {
      handleQueryableBoundsChange(mapBounds);
    }
  }, [mapBounds.e, mapBounds.n, mapBounds.s, mapBounds.w, mapRef]);

  useEffect(() => {
    debouncedMapEventTracking(mapBounds, mapLayerState);
  }, [mapLayerState, mapBounds]);

  useAfterMountedEffect(() => {
    WebAnalytics.mapLayerChanged(mapLayerState);
  }, [mapLayerState]);

  const onViewportChange = useCallback(
    (viewport: Partial<ViewState>, bounds: MapBounds) => {
      setMapBounds(bounds);
      updateLocationDebounced(viewport, bounds);
    },
    [paramsString]
  );
  const updateLocationDebounced = _.debounce((viewport: Partial<ViewState>, bounds: MapBounds) => {
    onLocationChange(viewport, bounds);
    dispatch(LocationActions.LOCATION_UPDATE(viewport));
  }, 300);

  const handleDimensionsChange = (width: number, height: number) => {
    if (
      width &&
      height &&
      mapRef &&
      (width !== mapDimensions.width || height !== mapDimensions.height)
    ) {
      setMapDimensions({ width, height });
      mapRef.getMap().resize();
    }
  };

  const onLocationChange = (viewport: Partial<ViewState>, bounds: MapBounds) => {
    replaceQueryString<ExtendedMapQueryParams>({
      lng: truncateTo6Decimal(viewport.longitude),
      lat: truncateTo6Decimal(viewport.latitude),
      zoom: viewport.zoom,
    });
    // if you pan around with a lake selected and select a new one before the onLocationChange event fires, the interactiveWaterbody.id does not match the interactiveWaterbodyId
    if (interactiveWaterbodyId && interactiveWaterbody?.id === interactiveWaterbodyId) {
      // check if the map bounds are completely outside the waterbody bounds
      const { bbox } = interactiveWaterbody;
      if (
        bounds.e < bbox[0][0] ||
        bounds.w > bbox[2][0] ||
        bounds.n < bbox[3][1] ||
        bounds.s > bbox[1][1]
      ) {
        setInteractiveWaterbodyId(null);
      }
    }
    // FIXME: this has stale interactiveWaterbodyId but I don't know why, non useCallback version has the same issue
    if (interactiveWaterbodyId && viewport.zoom <= MINIMUM_ZOOM) {
      setInteractiveWaterbodyId(null);
    }
  };

  useEffect(() => {
    if (!aerisController) {
      return;
    }
    if (mapLayerState[LAYER_TYPES.WEATHER].length > 0 && prevActiveModal === null && activeModal) {
      const dataInspectorControl = aerisController.controls.dataInspector;

      // have to check b/c water temp or clarity layers prevent dataInspector from being added at all
      dataInspectorControl?.hide();
    }
  }, [aerisController, activeModal, mapLayerState, prevActiveModal]);

  const fetchWaterbody = useCallback(async () => {
    const waterbodyRes = await apiV1.waterbodyFetch(interactiveWaterbodyId);
    return waterbodyRes.data.data;
  }, [interactiveWaterbodyId]);

  const fetchTempTiles = async (lats: number[], longs: number[]) => {
    try {
      setWaterTempShowComposite(true);
      setWaterTempTiles([]); // set to loading
      setWaterTempTilesLoadingState(LoadingState.PENDING);

      const tempTilesRes = await apiV1.mapTemperatureTilesFetch({
        max_latitude: Math.max(...lats),
        max_longitude: Math.max(...longs),
        min_latitude: Math.min(...lats),
        min_longitude: Math.min(...longs),
      });
      const tempTiles = tempTilesRes.data.data;
      const tilesWithMinimumCoverage = tempTiles.filter(tileMeetsMinimumCoverage);
      let selectedTile = tempTiles
        .filter((tile: MapSourceTile) => tile.coverage_pct >= TEMP_TILE_SELECTED_COVERAGE_PERCENT)
        .slice(-1)[0];
      if (!selectedTile && tilesWithMinimumCoverage.length > 0) {
        selectedTile = tilesWithMinimumCoverage.slice(-1)[0];
      }
      if (selectedTile) {
        const selectedTileIdx = tempTiles.findIndex(
          (t: MapSourceTile) => t.mapbox_tileset_id === selectedTile.mapbox_tileset_id
        );
        setWaterTempSliderIdx(selectedTileIdx);
        setWaterTempShowComposite(false);
      } else {
        setWaterTempSliderIdx(-1);
        setWaterTempShowComposite(true);
      }

      setWaterTempTiles(tempTiles);
      setWaterTempModalOpen(true);
      setWaterTempTilesLoadingState(LoadingState.DONE);
    } catch (error) {
      prevInteractiveWaterbodyIdRef.current = null;
      setWaterTempTilesLoadingState(LoadingState.ERROR);
    }
  };

  const fetchClarityTiles = async (lats: number[], longs: number[]) => {
    try {
      setWaterClarityShowComposite(true);
      setWaterClarityTiles([]); // set to loading
      setWaterClarityTilesLoadingState(LoadingState.PENDING);

      const tilesRes = await apiV1.mapClarityTilesFetch({
        max_latitude: Math.max(...lats),
        max_longitude: Math.max(...longs),
        min_latitude: Math.min(...lats),
        min_longitude: Math.min(...longs),
      });
      const tiles = tilesRes.data.data;
      const tilesWithMinimumCoverage = tiles.filter(tileMeetsMinimumCoverage);
      let selectedTile = tiles
        .filter((tile: MapSourceTile) => tile.coverage_pct >= TEMP_TILE_SELECTED_COVERAGE_PERCENT)
        .slice(-1)[0];
      if (!selectedTile && tilesWithMinimumCoverage.length > 0) {
        selectedTile = tilesWithMinimumCoverage.slice(-1)[0];
      }
      if (selectedTile) {
        const selectedTileIdx = tiles.findIndex(
          (t: MapSourceTile) => t.mapbox_tileset_id === selectedTile.mapbox_tileset_id
        );
        setWaterClaritySliderIdx(selectedTileIdx);
        setWaterClarityShowComposite(false);
      } else {
        setWaterClaritySliderIdx(-1);
        setWaterClarityShowComposite(true);
      }

      setWaterClarityTiles(tiles);
      setWaterClarityModalOpen(true);
      setWaterClarityTilesLoadingState(LoadingState.DONE);
    } catch (error) {
      prevInteractiveWaterbodyIdRef.current = null;
      setWaterClarityTilesLoadingState(LoadingState.ERROR);
    }
  };

  // fetch correct type of tiles & find the correct tile to display
  const onInteractiveWaterbodyIdChange = async () => {
    if (!interactiveWaterbodyId) {
      // if click outside waterbody boundary, close modal & show composite
      if (omniaWaterTempLayerActive) {
        setWaterTempModalOpen(false);
        setWaterTempShowComposite(true);
      } else {
        setWaterClarityModalOpen(false);
        setWaterClarityShowComposite(true);
      }
      return;
    }

    if (prevInteractiveWaterbodyIdRef.current === interactiveWaterbodyId) {
      if (omniaWaterTempLayerActive) {
        setWaterTempModalOpen(true);
        setWaterTempShowComposite(waterTempSliderIdx < 0); // if sliderIdx, don't show the composite
      }
      if (omniaWaterClarityLayerActive) {
        setWaterClarityModalOpen(true);
        setWaterClarityShowComposite(waterClaritySliderIdx < 0); // if sliderIdx, don't show the composite
      }
      prevInteractiveWaterbodyIdRef.current = interactiveWaterbodyId;
      return;
    }

    const _selectedWaterbody = await fetchWaterbody();
    setInteractiveWaterbody(_selectedWaterbody);
    const { bbox } = _selectedWaterbody;

    if (bbox) {
      const longs = bbox.map((b) => b[0]);
      const lats = bbox.map((b) => b[1]);
      omniaWaterTempLayerActive ? fetchTempTiles(lats, longs) : fetchClarityTiles(lats, longs);
    }
    prevInteractiveWaterbodyIdRef.current = interactiveWaterbodyId;
  };

  useEffect(() => {
    if (omniaWaterTempLayerActive || omniaWaterClarityLayerActive) {
      onInteractiveWaterbodyIdChange();
    }
  }, [interactiveWaterbodyId]);

  useEffect(() => {
    if (!omniaWaterTempLayerActive) {
      setWaterTempModalOpen(false);
      setWaterTempShowComposite(true);
      setInteractiveWaterbodyId(null);
      prevInteractiveWaterbodyIdRef.current = null;
    }
  }, [omniaWaterTempLayerActive]);

  useEffect(() => {
    if (!omniaWaterClarityLayerActive) {
      setInteractiveWaterbodyId(null);
      setWaterClarityShowComposite(true);
      setWaterClarityModalOpen(false);
      prevInteractiveWaterbodyIdRef.current = null;
    }
  }, [omniaWaterClarityLayerActive]);

  const handleWaypointClick = useCallback(
    (waypoint: Waypoint) => {
      WebAnalytics.mapInteracted({
        point: {
          lat: waypoint.lat,
          lon: waypoint.lng,
        },
        type: 'waypoint',
        type_id: waypoint.id.toString(),
      });

      if (editableWaypoint || newWaypoint) {
        discardModal(waypoint);
        return;
      }

      setSelectedWaypoint(waypoint);
      if (!isWaypointModalOpen) {
        handleModalChange('waypoints');
      }
    },
    [editableWaypoint, newWaypoint, activeModal, selectedWaypoint, isWaypointModalOpen]
  );

  const handleSelectedWaypointChange = useCallback(
    (waypoint: Waypoint | null) => {
      setSelectedWaypoint(waypoint);
      setShowWaypointDetailsBackButton(!!waypoint);
    },
    [mapRef]
  );

  const handleWaypointCreate = useCallback(() => {
    const { lat, lng }: mapboxgl.LngLat = mapRef.getCenter();
    setNewWaypoint({
      lat,
      lng,
      waypoint_type: 'marker',
      name: `New Waypoint ${waypointsFiltered.length + 1}`,
    });
  }, [mapRef, waypointsFiltered]);

  const discardModal = useCallback((waypoint?: Waypoint | undefined) => {
    Modal.confirm({
      centered: true,
      closable: true,
      content: 'Are you sure you want to discard changes to your Waypoint?',
      icon: null,
      okText: 'Discard',
      onOk() {
        setNewWaypoint(null);
        setEditableWaypoint(null);
        setSelectedWaypoint(waypoint || null);
        if (isMobile && !waypoint) {
          closeActiveModal();
        }
      },
      title: 'Discard Changes',
      wrapClassName: styles.antQuickModal,
    });
  }, []);

  const handleEditableWaypointChange = useCallback(
    (waypoint: Waypoint) => {
      setEditableWaypoint(waypoint);
    },
    [mapRef]
  );

  const checkWaypointMode = useCallback(() => {
    if (editableWaypoint || newWaypoint) {
      discardModal(editableWaypoint);
      return true;
    }
    return false;
  }, [editableWaypoint, newWaypoint]);

  const handleWaypointDragEnd = useCallback(
    (e: MarkerDragEvent) => {
      const waypointKind = newWaypoint ? 'new' : 'edit';
      const lat = e.lngLat.lat;
      const long = e.lngLat.lng;

      if (waypointKind === 'new') {
        setNewWaypoint({
          ...newWaypoint,
          lat,
          lng: long,
        });
      } else {
        setEditableWaypoint({
          ...editableWaypoint,
          lat,
          lng: long,
        });
      }
    },
    [editableWaypoint, newWaypoint]
  );

  useEffect(
    function waypointsLayerOnIfPresent() {
      const waypointsLayerInactive = !mapLayerState[LAYER_TYPES.POINTS_OF_INTEREST].includes(
        POINTS_OF_INTEREST_LAYER.WAYPOINTS
      );
      if (waypointsFiltered.length && waypointsLayerInactive) {
        onSelectPointsOfInterestLayer(POINTS_OF_INTEREST_LAYER.WAYPOINTS);
      }
    },
    [waypointsFiltered]
  );

  const handleQueryableBoundsChange = useCallback(
    _.debounce((bounds: MapBounds) => {
      if (isDesktop && isReportsModalOpen) {
        setQueryableBounds(getOpenModalBounds(mapRef, bounds));
        // TODO? set scroll position to top?
      } else {
        setQueryableBounds(bounds);
      }
    }, 300),
    [activeModal, mapRef, isReportsModalOpen, isDesktop]
  );

  useEffect(() => {
    if (!mapRef || isMobile) {
      return;
    }
    if (isReportsModalOpen) {
      setQueryableBounds((currentBounds) => getOpenModalBounds(mapRef, currentBounds));
    } else if (prevActiveModal === 'featuredReports') {
      setQueryableBounds((currentBounds) => getClosedModalBounds(mapRef, currentBounds));
    }
  }, [activeModal, mapRef, isMobile]);

  const handleModalChange = useCallback(
    (
      modalType: ActiveMapPageModalType | null,
      e?: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      e?.stopPropagation();
      // if editing / creating waypoint show discard modal
      if (checkWaypointMode()) {
        return;
      }
      const isModalOpen = activeModal === modalType;
      setActiveModal(isModalOpen ? null : modalType);

      // TODO: move these side effects to caller or separate functions
      // when closing the lakes modal, deselect waterbody and tab
      const closingModal = !modalType || isModalOpen;
      if (waterbodyQuery && (closingModal || modalType !== 'lakes')) {
        replaceQueryString<ExtendedMapQueryParams>({
          waterbody_slug: null,
          tab: null,
        });
      }
      // when closing the featured reports modal, deselect fishing report
      if (closingModal || modalType !== 'featuredReports') {
        setSelectedFishingReport(null);
      }
    },
    [activeModal, checkWaypointMode, paramsString]
  );
  const closeActiveModal = () => {
    handleModalChange(null);
  };

  const handleFilterChange = useCallback<FiltersScreenProps['onFilterChange']>(
    (filters) => {
      setSelectedWaterbodyIds(filters.selectedWaterbodyIds);
      setSelectedWaypointTypes(filters.selectedWaypointTypes);
      setSelectedStartDate(filters.selectedStartDate);
      setSelectedEndDate(filters.selectedEndDate);
      setSelectedWindDirections(filters.selectedWindDirections);
      setSelectedWindSpeeds(filters.selectedWindSpeeds);
      setSelectedCloudCovers(filters.selectedCloudCovers);
      setSelectedBarometricPressures(filters.selectedBarometricPressures);
      setSelectedBarometricPressureTrends(filters.selectedBarometricPressureTrends);
    },
    [selectedWaterbodyIds, selectedWaypointTypes]
  );

  const handleSharedWaypointModalClose = useCallback(() => {
    handleModalChange('waypoints');
    setSharedWaypoint(null);
    pushQueryString<ExtendedMapQueryParams>({
      [GlobalQueryParams.waypoint_hash]: '',
    });
  }, [activeModal, paramsString, interactiveWaterbody?.id, interactiveWaterbodyId]);

  const handleWaypointModalClose = useCallback(() => {
    handleModalChange('waypoints');
    setSelectedWaypoint(null);
    setShowWaypointDetailsBackButton(false);
  }, [handleModalChange, activeModal]);

  const handlePinClick = useCallback(
    (waterbody) => {
      if (omniaWaterTempLayerActive || omniaWaterClarityLayerActive) {
        setInteractiveWaterbodyId(waterbody.id);
        if (omniaWaterTempLayerActive) {
          setWaterTempModalOpen(true);
        }
        if (omniaWaterClarityLayerActive) {
          setWaterClarityModalOpen(true);
        }
      }
      replaceQueryString<ExtendedMapQueryParams>({
        waterbody_slug: waterbody.url_slug,
      });
    },
    [omniaWaterTempLayerActive, omniaWaterClarityLayerActive, paramsString]
  );

  const isBoatLandingModalOpen = !!selectedBoatLanding?.AccessName;

  const updateIconSize = useCallback((name: string, map: mapboxgl.Map) => {
    if (name) {
      map.setLayoutProperty(InteractiveLayerIds.BOAT_LANDINGS, 'icon-size', [
        'match',
        ['get', 'AccessName'],
        name,
        0.5,
        0.25,
      ]);
    } else {
      map.setLayoutProperty(InteractiveLayerIds.BOAT_LANDINGS, 'icon-size', 0.25);
    }
  }, []);

  useEffect(() => {
    if (mapRef) {
      const map = mapRef.getMap();
      updateIconSize(selectedBoatLanding?.AccessName, map);
    }
  }, [selectedBoatLanding]);

  const handleBoatLandingClick = useCallback((boatLanding: MapboxBoatLanding) => {
    setSelectedBoatLanding(boatLanding);
    WebAnalytics.mapInteracted({
      point: {
        lat: boatLanding.Latitude,
        lon: boatLanding.Longitude,
      },
      type: 'boat_landing',
      type_id: `${boatLanding.HUC12}_${boatLanding.HUC12_Name}`,
    });
  }, []);

  const handleMapClick = useCallback(
    (e: mapboxgl.MapLayerMouseEvent) => {
      const { features } = e;

      const boatLandingFeature = features.find(
        (feature) => feature.layer.id === InteractiveLayerIds.BOAT_LANDINGS
      );
      const waypointFeature = features.find(
        (feature) => feature.layer.id === InteractiveLayerIds.WAYPOINTS
      );

      if (boatLandingFeature) {
        return handleBoatLandingClick(boatLandingFeature.properties as MapboxBoatLanding);
      }

      if (waypointFeature) {
        const waterbody = waypointFeature.properties.waterbody;
        const parsedWaypointWaterbody = waterbody && JSON.parse(waterbody);
        return handleWaypointClick({
          ...(waypointFeature.properties as Waypoint),
          waterbody: parsedWaypointWaterbody || null,
        });
      }

      const waterbodyOutlineFeatures = features.filter((feature) =>
        isInteractiveWaterbodyFeature(feature.properties)
      );
      const hasWaterbodyOutlineFeature = waterbodyOutlineFeatures.length > 0;
      if (omniaWaterTempLayerActive || omniaWaterClarityLayerActive) {
        if (!hasWaterbodyOutlineFeature) {
          setInteractiveWaterbodyId(null);
        }
        waterbodyOutlineFeatures.forEach((feature) => {
          const dataPointProperties = feature.properties;
          if (zoomQuery && Number(zoomQuery) > MINIMUM_ZOOM) {
            setInteractiveWaterbodyId(dataPointProperties.waterbody_id);
            if (omniaWaterTempLayerActive) {
              setWaterTempModalOpen(true);
            } else {
              setWaterClarityModalOpen(true);
            }
          }
        });
      }

      waterbodyOutlineFeatures.forEach((feature) => {
        if (feature.properties.waterbody_slug) {
          replaceQueryString<ExtendedMapQueryParams>({
            waterbody_slug: feature.properties.waterbody_slug,
          });
        }
      });

      const waterTempFeatures = features.filter(isWaterTempFeature);
      waterTempFeatures.forEach((feature) => {
        WebAnalytics.mapFirstPartyLayerInteracted({
          layer_type: 'historical_temp',
          interaction_type: 'waterbody',
          waterbody_id: interactiveWaterbodyId,
        });
        setWaterTempPopupInfo({
          waterTemp: Math.round(feature.properties.val),
          lat: e.lngLat.lat,
          lng: e.lngLat.lng,
        });
      });

      const waterClarityFeatures = features.filter(isWaterClarityFeature);
      waterClarityFeatures.forEach((feature) => {
        WebAnalytics.mapFirstPartyLayerInteracted({
          layer_type: 'clarity',
          interaction_type: 'waterbody',
          waterbody_id: interactiveWaterbodyId,
        });
        setWaterClarityPopupInfo({
          waterClarity: Math.round(feature.properties.val),
          lat: e.lngLat.lat,
          lng: e.lngLat.lng,
        });
      });
    },
    [
      omniaWaterTempLayerActive,
      omniaWaterClarityLayerActive,
      zoomQuery,
      mapRef,
      selectedBoatLanding,
      activeModal,
      selectedWaypoint,
      editableWaypoint,
      newWaypoint,
      markers.map((marker) => marker.id).join(','),
    ]
  );

  useAfterMountedEffect(
    function handleNavigateBackToWaypointURL() {
      if (props.sharedWaypoint && sharedWaypointQuery && !sharedWaypoint) {
        setSharedWaypoint(props.sharedWaypoint);
      }
    },
    [props.sharedWaypoint, sharedWaypointQuery]
  );

  useAfterMountedEffect(() => {
    if (sharedWaypoint && !isWaypointModalOpen) {
      handleModalChange('waypoints');
    }
  }, [sharedWaypoint]);

  const updatedLatLng = useRef<boolean>(false);
  useEffect(
    function recenterMobileSharedWaypointMapDown() {
      if (
        props.sharedWaypoint &&
        sharedWaypointQuery &&
        sharedWaypoint &&
        mapRef &&
        isMobile &&
        !updatedLatLng.current
      ) {
        const { lat, lng } = sharedWaypoint;
        const args = { map: mapRef.getMap(), point: { lat, lng }, yAdjust: -30 };
        const changedLatLng = adjustLatLng(args);
        updatedLatLng.current = true;
        replaceQueryString<ExtendedMapQueryParams>({
          lat: changedLatLng.lat,
        });
        mapRef.flyTo({
          center: [changedLatLng.lng, changedLatLng.lat],
          zoom: 14,
          duration: 50,
        });
      }
    },
    [mapRef, props.sharedWaypoint, sharedWaypoint, isMobile]
  );

  useEffect(
    function recenterMobileLakePinDown() {
      if (mapRef && isMobile && externalSearchQuery) {
        const args = {
          map: mapRef.getMap(),
          point: { lat: latitude, lng: longitude },
          yAdjust: -30,
        };
        const changedLatLng = adjustLatLng(args);
        mapRef.flyTo({
          center: [changedLatLng.lng, changedLatLng.lat],
          duration: 20,
        });
      }
    },
    [mapRef, isMobile, externalSearchQuery]
  );

  const clearWaterbody = useCallback(() => {
    replaceQueryString<ExtendedMapQueryParams>({ waterbody_slug: null });
    setSelectedWaterbody(null);
    setWaterbodyLoadingState(LoadingState.NOT_STARTED);
  }, [paramsString]);

  useEffect(() => {
    if (waterbodyQuery) {
      setSelectedWaterbody(null);
      setWaterbodyLoadingState(LoadingState.PENDING);
      apiV1.waterbodyFetch(waterbodyQuery).then((res) => {
        setSelectedWaterbody(res.data.data);
        setWaterbodyLoadingState(LoadingState.DONE);
      });
      setActiveModal('lakes');
    } else {
      clearWaterbody();
    }
  }, [waterbodyQuery]);

  const { userWaterbodies } = useUserWaterbodies();
  const [waterTempPopupInfo, setWaterTempPopupInfo] = useState<{
    waterTemp: number;
    lat: number;
    lng: number;
  } | null>(null);
  const [waterClarityPopupInfo, setWaterClarityPopupInfo] = useState<{
    waterClarity: number;
    lat: number;
    lng: number;
  } | null>(null);

  // clear the popup whenever you change the date so the date isn't wrong
  useEffect(() => {
    setWaterTempPopupInfo(null);
  }, [waterTempDisplayedDate?.unix()]);

  // clear the popup if omnia water temp is not active
  useEffect(() => {
    if (mapLayerState[LAYER_TYPES.CORE] !== CORE_LAYER.OMNIA_WATER_TEMP) {
      setWaterTempPopupInfo(null);
    }
    if (mapLayerState[LAYER_TYPES.CORE] !== CORE_LAYER.CLARITY) {
      setWaterClarityPopupInfo(null);
    }
  }, [mapLayerState]);

  useEffect(() => {
    setWaterClarityPopupInfo(null);
  }, [waterClarityDisplayedDate?.unix()]);

  const handleMapLoaded = useCallback((_bounds: MapBounds, _mapRef) => {
    setMapBounds(_bounds);
    setMapRef(_mapRef);
    if (selectedBoatLanding?.AccessName) {
      updateIconSize(selectedBoatLanding?.AccessName, _mapRef.getMap());
    }
  }, []);

  const handleContentModalClose = useCallback(() => {
    let newZoom = 12;
    const width = measureBounds.width || window.innerWidth;
    const height = measureBounds.height || window.innerHeight;

    if (modalWaterbody.bbox && width && height) {
      newZoom = fitBounds(
        [
          [modalWaterbody.bbox[0][0], modalWaterbody.bbox[1][1]],
          [modalWaterbody.bbox[2][0], modalWaterbody.bbox[3][1]],
        ],
        {
          width,
          height,
        }
      ).zoom;
    }

    setModalContentOpen(false);
    setTimeout(() => {
      const params = modalWaterbody
        ? {
            lat: modalWaterbody.lat,
            lng: modalWaterbody.lng,
            zoom: newZoom,
          }
        : {};
      history.push({
        pathname: RoutePaths.MAP,
        search: omniaStringify(params),
      });
    }, 300); // same timing as transition
  }, [measureBounds.width, measureBounds.height, modalWaterbody]);

  const handleModalWaterbodyClick = useCallback(
    (w: Waterbody) => {
      let newZoom = 12;
      const width = measureBounds.width || window.innerWidth;
      const height = measureBounds.height || window.innerHeight;

      if (w.bbox && width && height) {
        newZoom = fitBounds(
          [
            [w.bbox[0][0], w.bbox[1][1]],
            [w.bbox[2][0], w.bbox[3][1]],
          ],
          {
            width,
            height,
          }
        ).zoom;
      }
      mapRef.flyTo({
        center: [w.lng, w.lat],
        zoom: newZoom,
        duration: 1500,
      });
      replaceQueryString<ExtendedMapQueryParams>({
        waterbody_slug: w.url_slug,
      });
    },
    [paramsString, measureBounds.width, measureBounds.height, mapRef]
  );

  return (
    <ScrollToTop>
      <section className={styles.mapPage}>
        {seo ? seo : <MapPageSEO sharedWaypoint={sharedWaypoint} />}
        <section className={styles.mapAndSidebarContainer}>
          <div className={styles.mapContainer}>
            <section
              className={styles.map}
              style={{
                minHeight: isDesktop && headerHeight ? `calc(100dvh - ${headerHeight}px)` : 'auto',
                minWidth: isDesktop ? 'calc(100vw - 252px)' : 'auto',
              }}
            >
              <div className={styles.wrapper} ref={measureRef}>
                {measureBounds.width != null && (
                  <LoadableMap
                    draggableWaypoint={editableWaypoint || newWaypoint}
                    // featuredMarkers={featuredMarkers}
                    selectedFishingReport={selectedFishingReport}
                    getAerisController={setAerisController}
                    interactiveWaterbodyId={interactiveWaterbodyId}
                    height={measureBounds.height}
                    highlightedWaterbody={highlightedWaterbody}
                    loadInteractiveImages
                    mapLayerState={mapLayerState}
                    markers={markers}
                    navControl
                    navControlPosition={'bottom-right'}
                    onPinClick={handlePinClick}
                    onLoad={handleMapLoaded}
                    onMapClick={handleMapClick}
                    onViewportChange={onViewportChange}
                    onWaypointDragEnd={handleWaypointDragEnd}
                    popup={
                      omniaWaterTempLayerActive ? (
                        <WaterTempPopup
                          waterTempPopupInfo={waterTempPopupInfo}
                          onClose={() => setWaterTempPopupInfo(null)}
                          waterTempWaterbodyName={interactiveWaterbody?.primary_name}
                          waterTempDisplayedDate={waterTempDisplayedDate}
                        />
                      ) : (
                        omniaWaterClarityLayerActive && (
                          <WaterClarityPopup
                            waterClarityPopupInfo={waterClarityPopupInfo}
                            onClose={() => setWaterClarityPopupInfo(null)}
                            waterClarityWaterbodyName={interactiveWaterbody?.primary_name}
                            waterClarityDisplayedDate={waterClarityDisplayedDate}
                          />
                        )
                      )
                    }
                    selectedBoatLanding={selectedBoatLanding}
                    selectedWaterbody={selectedWaterbody}
                    selectedWaypoint={sharedWaypoint || selectedWaypoint}
                    usePreciseLocation
                    userFavoriteWaterbodies={userWaterbodies}
                    viewport={currentViewport}
                    waypoints={waypointsFiltered}
                    waterClarityDailyTile={waterClaritySliderTile?.mapbox_tileset_id}
                    waterClarityShowComposite={waterClarityShowComposite}
                    waterTempDailyTile={waterTempSliderTile?.mapbox_tileset_id}
                    waterTempShowComposite={waterTempShowComposite}
                    width={measureBounds.width}
                  />
                )}
              </div>
            </section>

            <div className={styles.sidebar}>
              <div
                className={classNames(styles.sidebarItem, {
                  [styles.sidebarItem__selected]: isBaseModalOpen,
                })}
                onClick={(e) => {
                  handleModalChange('base', e);
                }}
              >
                <span className={styles.sidebarItemIcon}>
                  <SvgNavTerrainView />
                </span>
                <span>Terrain View</span>
              </div>

              <div
                className={classNames(styles.sidebarItem, {
                  [styles.sidebarItem__selected]: isReportsModalOpen,
                })}
                onClick={(e) => handleModalChange('featuredReports', e)}
              >
                <div className={styles.sidebarItemIcon}>
                  <SvgNavFishingReports />
                </div>
                <span>Fishing Reports</span>
              </div>

              <div
                className={classNames(styles.sidebarItem, {
                  [styles.sidebarItem__selected]: isHotBaitsModalOpen,
                })}
                onClick={(e) => handleModalChange('hotbaits', e)}
              >
                <div className={styles.sidebarItemIcon}>
                  <SvgNavHotBaits />
                </div>
                <span>Hotbaits</span>
              </div>

              <div
                className={classNames(styles.sidebarItem, {
                  [styles.sidebarItem__selected]: isPointsOfInterestModalOpen,
                })}
                onClick={(e) => {
                  handleModalChange('points_of_interest', e);
                }}
              >
                <div className={styles.sidebarItemIcon}>
                  <SvgNavMapPins />
                </div>
                <span>Map Pins</span>
              </div>

              <div
                className={classNames(styles.sidebarItem, {
                  [styles.sidebarItem__selected]: isCoreModalOpen,
                })}
                onClick={(e) => {
                  handleModalChange('core', e);
                }}
              >
                <div className={styles.sidebarItemIcon}>
                  <SvgNavWaterLayers />
                </div>
                <span>Water Layers</span>
              </div>

              <div
                className={classNames(styles.sidebarItem, {
                  [styles.sidebarItem__selected]: isWeatherModalOpen,
                })}
                onClick={(e) => {
                  handleModalChange('weather', e);
                }}
              >
                <div className={styles.sidebarItemIcon}>
                  <SvgNavWeatherLayers />
                </div>
                <span>Weather Layers</span>
              </div>
              {/* <div
              className={classNames(styles.sidebarItem, styles.sidebarItem__mapIcon, {
                [styles.sidebarItem__selected]: isLakesModalOpen,
              })}
              onClick={(e) => handleModalChange('lakes', e)}
            >
              <div className={styles.sidebarItemIcon}>
                <SvgLakes />
              </div>
              Lakes
            </div> */}
              <div
                className={classNames(styles.sidebarItem, styles.sidebarItem__mapIcon, {
                  [styles.sidebarItem__selected]: isWaypointModalOpen,
                })}
                onClick={(e) => {
                  handleModalChange('waypoints', e);
                  if (isWaypointModalOpen) {
                    setSelectedWaypoint(null);
                    setShowWaypointDetailsBackButton(false);
                  }
                }}
              >
                <div className={styles.sidebarItemIcon}>
                  <SvgNavMyWaypoints />
                </div>
                My Waypoints
              </div>

              <div
                className={classNames(styles.sidebarItem, {
                  [styles.sidebarItem__selected]: isMyLakesModalOpen,
                })}
                onClick={(e) => {
                  handleModalChange('my_lakes', e);
                }}
              >
                <div className={styles.sidebarItemIcon}>
                  <SvgNavMyLakes />
                </div>
                <span>My Lakes</span>
              </div>
            </div>

            <section className={styles.searchAndFilters}>
              {(isDesktop || searchIsOpen) && (
                <SearchAndFilters
                  handleModalChange={handleModalChange}
                  isReportsModalOpen={isReportsModalOpen}
                  mapDimensions={mapDimensions}
                  mapRef={mapRef}
                />
              )}
            </section>

            <section className={styles.pinFilters}>
              <button
                className={styles.pinFiltersButton}
                onClick={() => {
                  setIsFiltersOpen(!isFiltersOpen);
                }}
              >
                <span>Filter Map {pinFilterCount > 0 && `(${pinFilterCount})`}</span>
                <DownCircleOutlined className={styles.pinFiltersButtonIcon} />
              </button>
            </section>

            {!isPro && (
              <OmniaButton
                className={'absolute bottom-[12px] sm:bottom-7 right-16 z-[200]'}
                onPress={() => {
                  openProUpsellModal();

                  WebAnalytics.proButtonClicked({
                    location: 'map_sidebar',
                    type: 'trial',
                    style: 'button',
                  });
                }}
                kind="primary"
                size="md"
              >
                <span>
                  Try <span className="text-[#002b3a]">Free</span>
                  <br /> 7-Day Trial
                </span>
              </OmniaButton>
            )}

            <FeaturedReportsModal
              fishingReports={fishingReports}
              fishingReportsLoading={fishingReportsLoading}
              fishingReportsLoadingComplete={fihsingReportsLoadingComplete}
              fishingReportsCountTotal={fishingReportsCountTotal}
              isModalOpen={isReportsModalOpen}
              mapRef={mapRef}
              onClose={handleModalChange}
              onSeasonGroupSelect={(seasonGroupName) => {
                replaceQueryString<ExtendedMapQueryParams>({
                  season_group: seasonGroupName,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FeaturedReportsModal',
                  species: speciesQuery,
                  season_group: seasonGroupName,
                  lunker: lunkerQuery,
                });
              }}
              onSpeciesSelect={(speciesName) => {
                replaceQueryString<ExtendedMapQueryParams>({
                  species: speciesName,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FeaturedReportsModal',
                  species: speciesName,
                  season_group: seasonGroupQuery,
                  lunker: lunkerQuery,
                });
              }}
              onReportHover={(report) => {
                const waterbody = report?.waterbody || null;
                setHighlightedWaterbody(waterbody);
              }}
              loadMoreElementRef={loadMoreElementRef}
              onWaterbodyClick={handleModalWaterbodyClick}
            />

            <FeaturedHotbaitsModal
              fishingReports={fishingReports}
              fishingReportsLoading={fishingReportsLoading}
              fishingReportsLoadingComplete={fihsingReportsLoadingComplete}
              fishingReportsCountTotal={fishingReportsCountTotal}
              isModalOpen={isHotBaitsModalOpen}
              mapRef={mapRef}
              onClose={handleModalChange}
              onSeasonGroupSelect={(seasonGroupName) => {
                replaceQueryString<ExtendedMapQueryParams>({
                  season_group: seasonGroupName,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FeaturedHotbaitsModal',
                  species: speciesQuery,
                  season_group: seasonGroupName,
                  lunker: lunkerQuery,
                });
              }}
              onSpeciesSelect={(speciesName) => {
                replaceQueryString<ExtendedMapQueryParams>({
                  species: speciesName,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FeaturedHotbaitsModal',
                  species: speciesName,
                  season_group: seasonGroupQuery,
                  lunker: lunkerQuery,
                });
              }}
              onReportHover={(report) => {
                const waterbody = report?.waterbody || null;
                setHighlightedWaterbody(waterbody);
              }}
              onWaterbodyClick={handleModalWaterbodyClick}
              loadMoreElementRef={loadMoreElementRef}
            />

            <LakesModal
              isModalOpen={isLakesModalOpen}
              mapRef={mapRef}
              waterbodies={markers}
              waterbodiesLoadingState={waterbodiesLoadingState}
              onClose={closeActiveModal}
              waterbodySlug={waterbodyQuery}
              waterbody={selectedWaterbody}
              waterbodyLoadingState={waterbodyLoadingState}
            />

            <WaypointsModal
              allWaypoints={waypointsAll}
              discardModal={discardModal}
              editableWaypoint={editableWaypoint}
              isModalOpen={isWaypointModalOpen}
              waypointsLoading={waypointsFilteredLoading && waypointsAllLoading}
              mapRef={mapRef}
              newWaypoint={newWaypoint}
              onClose={handleWaypointModalClose}
              onEditableWaypointChange={handleEditableWaypointChange}
              onFilterChange={handleFilterChange}
              onNewWaypointChange={setNewWaypoint}
              onSelectedWaypointChange={handleSelectedWaypointChange}
              onSharedWaypointModalClose={handleSharedWaypointModalClose}
              onWaypointCreate={handleWaypointCreate}
              selectedBarometricPressures={selectedBarometricPressures}
              selectedBarometricPressureTrends={selectedBarometricPressureTrends}
              selectedCloudCovers={selectedCloudCovers}
              selectedEndDate={selectedEndDate}
              selectedStartDate={selectedStartDate}
              selectedWaterbodyIds={selectedWaterbodyIds}
              selectedWaypoint={selectedWaypoint}
              selectedWaypointTypes={selectedWaypointTypes}
              selectedWindDirections={selectedWindDirections}
              selectedWindSpeeds={selectedWindSpeeds}
              sharedWaypoint={sharedWaypoint}
              showWaypointDetailsBackButton={showWaypointDetailsBackButton}
              waypointsToRender={waypointsFiltered}
            />

            <MyLakesModal
              isOpen={isMyLakesModalOpen}
              onClose={closeActiveModal}
              onWaterbodyClick={(w) => {
                mapRef.flyTo({
                  center: [w.lng, w.lat],
                  zoom: 12,
                  duration: 1500,
                });
              }}
            />

            <PinFiltersModal
              isOpen={isFiltersOpen}
              onClose={() => {
                setIsFiltersOpen(false);
              }}
              onSpeciesClick={(speciesName) => {
                replaceQueryString<ExtendedMapQueryParams>({
                  species: speciesName,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FilterModal',
                  species: speciesName,
                  season_group: seasonGroupQuery,
                  lunker: lunkerQuery,
                });
              }}
              onSeasonGroupClick={(seasonGroupName) => {
                replaceQueryString<ExtendedMapQueryParams>({
                  season_group: seasonGroupName,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FilterModal',
                  species: speciesQuery,
                  season_group: seasonGroupName,
                  lunker: lunkerQuery,
                });
              }}
              onLunkerClick={(lunker) => {
                const lunkerVal = lunker ? true : null;
                replaceQueryString<ExtendedMapQueryParams>({
                  lunker: lunkerVal,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FilterModal',
                  species: speciesQuery,
                  season_group: seasonGroupQuery,
                  lunker: lunkerVal,
                });
              }}
              onClearAll={() => {
                replaceQueryString<ExtendedMapQueryParams>({
                  species: null,
                  season_group: null,
                  lunker: null,
                });
                WebAnalytics.mapFiltersChanged({
                  origin: 'FilterModal',
                  species: null,
                  season_group: null,
                  lunker: false,
                });
              }}
            />

            <WaterTempSliderModal
              displayedDate={waterTempDisplayedDate}
              loadingState={waterTempTilesLoadingState}
              onChange={(v) => {
                const tile = waterTempTiles[v];
                if (tileMeetsMinimumCoverage(tile)) {
                  setWaterTempSliderIdx(v);
                }
                WebAnalytics.mapFirstPartyLayerInteracted({
                  layer_type: 'historical_temp',
                  interaction_type: 'slider',
                  waterbody_id: interactiveWaterbodyId,
                });
              }}
              onClose={() => {
                setWaterTempModalOpen(false);
              }}
              open={waterTempModalOpen}
              sliderIdx={waterTempSliderIdx}
              tiles={waterTempTiles}
            />
            <WaterClaritySliderModal
              displayedDate={waterClarityDisplayedDate}
              loadingState={waterClarityTilesLoadingState}
              onChange={(v) => {
                const tile = waterClarityTiles[v];
                if (tileMeetsMinimumCoverage(tile)) {
                  setWaterClaritySliderIdx(v);
                }
                WebAnalytics.mapFirstPartyLayerInteracted({
                  layer_type: 'clarity',
                  interaction_type: 'slider',
                  waterbody_id: interactiveWaterbodyId,
                });
              }}
              onClose={() => {
                setWaterClarityModalOpen(false);
              }}
              sliderIdx={waterClaritySliderIdx}
              tiles={waterClarityTiles}
              open={waterClarityModalOpen}
              key={waterClarityTiles.map((t) => t.mapbox_tileset_id).join()}
            />
            <ForecastModal
              aerisController={aerisController}
              open={hasForecastableLayer && !waterClarityModalOpen && !waterTempModalOpen}
            />
            <BoatLandingModal
              boatLanding={selectedBoatLanding}
              onClose={() => setSelectedBoatLanding(null)}
              isOpen={isBoatLandingModalOpen}
            />
            <BaseModal
              mapLayerState={mapLayerState}
              onBaseLayerChange={setBaseLayer}
              onClose={() => handleModalChange(null)}
              isOpen={isBaseModalOpen}
            />
            <PointsOfInterestModal
              mapLayerState={mapLayerState}
              onPointsOfInterestLayerChange={onSelectPointsOfInterestLayer}
              onOverlayLayersChange={onSelectOverlayLayer}
              isOpen={isPointsOfInterestModalOpen}
            />
            <WaterLayersModal
              mapLayerState={mapLayerState}
              isOpen={isCoreModalOpen}
              onCoreLayerChange={onSelectCoreLayer}
            />
            <WeatherLayerModal
              mapLayerState={mapLayerState}
              onWeatherLayerChange={onSelectWeatherLayer}
              isOpen={isWeatherModalOpen}
            />
            <LayersQuickToggleMenu
              activeModal={activeModal}
              isWaypointCreateMode={!!newWaypoint || !!editableWaypoint}
              onModalChange={(shouldOpen: boolean, type: ActiveMapPageModalType) =>
                handleModalChange(getUpdatedModalType(shouldOpen, type))
              }
              onWaypointCreate={handleWaypointCreate}
            />

            <MobileContentToggleMenu
              activeModal={activeModal}
              isWaypointCreateMode={!!newWaypoint || !!editableWaypoint}
              lakesCount={markers.length}
              onModalChange={(shouldOpen, layer) =>
                handleModalChange(getUpdatedModalType(shouldOpen, layer))
              }
              onWaypointCreate={handleWaypointCreate}
              onSearchClick={() => {
                setSearchIsOpen(!searchIsOpen);
              }}
              isSearchActive={searchIsOpen}
            />
            <LayersModal
              isModalOpen={isLayersModalOpen}
              mapLayerState={mapLayerState}
              onCoreLayerChange={onSelectCoreLayer}
              onOverlayLayersChange={onSelectOverlayLayer}
              onSelectPointsOfInterestLayer={onSelectPointsOfInterestLayer}
              onWeatherLayerChange={onSelectWeatherLayer}
              onModalChange={(val) => {
                handleModalChange(getUpdatedModalType(val, 'layers'));
              }}
            />
            <LegendModal
              isModalOpen={isLegendModalOpen}
              onModalChange={(val) => handleModalChange(getUpdatedModalType(val, 'legend'))}
              mapLayerState={mapLayerState}
            />
          </div>

          <MobileOnly tag="div">
            {isReportsModalOpen && (
              <OmniaButton
                size="lg"
                kind="secondary"
                className={styles.viewMapButton}
                onPress={() => handleModalChange(null)}
              >
                View Map
              </OmniaButton>
            )}
          </MobileOnly>
        </section>
      </section>
      {modalContent && (
        <>
          <div
            className={`fixed inset-0 bg-black/45 h-full z-[1000] transition-opacity duration-300 cursor-pointer ${
              modalContentOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="Modal overlay"
          />
          <div
            className={`fixed inset-0 z-[1000] transition-all duration-300 overflow-auto overscroll-contain ${
              modalContentOpen ? 'opacity-100' : 'opacity-0  scale-90'
            }`}
            onClick={handleContentModalClose}
          >
            <div
              className="relative top-[30dvh] sm:top-12 pointer-events-none px-3 sm:px-6"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="pointer-events-auto bg-white max-w-[1200px] mx-auto relative">
                {modalContent}
                <div
                  className="absolute top-0 right-0 flex items-center justify-center h-12 w-12 z-[1000] cursor-pointer"
                  onClick={handleContentModalClose}
                  aria-label="Close modal"
                >
                  <CloseOutlined />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </ScrollToTop>
  );
};
export default MapPage;
